// Localização: frontend/src/hooks/useDashboard.js (versão 0.2.0 - React Query)
/**
 * Hook para buscar dados resumidos do Dashboard usando React Query.
 * 
 * Migrado de useState/useEffect para React Query para melhor gerenciamento
 * de cache, retry automático e invalidação de dados.
 * 
 * @returns {object} Dados, estados de carregamento e erro.
 * @property {object} summary - Dados do resumo do dashboard
 * @property {boolean} isLoading - Indica se está carregando
 * @property {boolean} isError - Indica se houve erro
 * @property {Error} error - Objeto de erro (se houver)
 * @property {function} refetch - Função para recarregar os dados manualmente
 */

import { useQuery } from '@tanstack/react-query';
import { getDashboardSummary } from '../api/api';
import { useAuth } from '../context/AuthContext';
import { logHttpError } from '../utils/errorLogger';
import { getErrorMessage } from '../utils/errorMessages';

// Query key para cache do React Query
export const DASHBOARD_SUMMARY_QUERY_KEY = ['dashboard', 'summary'];

/**
 * Hook para buscar dados resumidos do Dashboard.
 * 
 * Configurações do React Query:
 * - staleTime: 5 minutos (dados considerados frescos por 5 minutos)
 * - cacheTime: 10 minutos (dados mantidos em cache por 10 minutos após desmontagem)
 * - refetchInterval: 5 minutos (refetch automático a cada 5 minutos)
 * - retry: 3 tentativas com backoff exponencial
 * - enabled: apenas quando usuário está autenticado
 * 
 * @returns {object} { summary, isLoading, isError, error, refetch }
 */
export const useDashboard = () => {
    const { isAuthenticated } = useAuth();

    const {
        data: response,
        isLoading,
        isError,
        error,
        refetch,
    } = useQuery({
        queryKey: DASHBOARD_SUMMARY_QUERY_KEY,
        queryFn: async () => {
            try {
                const response = await getDashboardSummary();
                return response.data;
            } catch (err) {
                // Loga o erro antes de relançar
                logHttpError(err, {
                    component: 'useDashboard',
                    endpoint: '/dashboard/summary',
                });
                throw err;
            }
        },
        enabled: isAuthenticated, // Só executa se usuário estiver autenticado
        staleTime: 1000 * 60 * 5, // 5 minutos - dados considerados frescos
        gcTime: 1000 * 60 * 10, // 10 minutos - tempo de cache (anteriormente cacheTime)
        refetchInterval: false, // Desabilitado - será controlado pelo useRealtimeData quando necessário
        refetchOnWindowFocus: false, // Não refetch ao focar na janela
        refetchOnMount: false, // Não refetch ao montar - usa cache se dados estão frescos
        refetchOnReconnect: true, // Refetch ao reconectar
        retry: (failureCount, error) => {
            // Não tenta novamente em erros 401, 403, 404
            if (error?.response?.status === 401 || 
                error?.response?.status === 403 || 
                error?.response?.status === 404) {
                return false;
            }
            // Tenta até 3 vezes para outros erros
            return failureCount < 3;
        },
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Backoff exponencial
        // Tratamento de erro personalizado
        throwOnError: false, // Não lança erro, apenas retorna no objeto error
    });

    // Extrair dados do response (mantendo compatibilidade com código existente)
    const summary = response?.data || null;

    // Manter compatibilidade: retornar error como string (se houver)
    // Usa mensagem amigável baseada no tipo de erro
    const errorMessage = error
        ? getErrorMessage(error, {
            context: 'dashboard',
            action: 'summary',
        })
        : null;

    return {
        summary,
        isLoading,
        // Manter compatibilidade: isError como boolean
        isError,
        // Manter compatibilidade: error como string (para compatibilidade com código existente)
        error: errorMessage,
        // Função para recarregar manualmente
        refetch,
    };
};

/**
 * Função auxiliar para invalidar o cache do dashboard.
 * Útil para invalidar após mutações (criar tarefa, atualizar status, etc.)
 * 
 * Exemplo de uso:
 * ```javascript
 * import { useQueryClient } from '@tanstack/react-query';
 * import { DASHBOARD_SUMMARY_QUERY_KEY } from './useDashboard';
 * 
 * const queryClient = useQueryClient();
 * 
 * // Após criar uma tarefa
 * queryClient.invalidateQueries({ queryKey: DASHBOARD_SUMMARY_QUERY_KEY });
 * ```
 */
export const invalidateDashboardCache = (queryClient) => {
    queryClient.invalidateQueries({ queryKey: DASHBOARD_SUMMARY_QUERY_KEY });
};
